import { RECOMMEND_THRESHOLD } from '../../constants';
import type {
    Drone,
    ModelServiceIntervalMap,
    PartId,
    PartType,
    Recommendation,
    ServiceIntervalMap,
    Urgency
} from '../../types';
import { getDroneIntervals } from '../getDroneIntervals';
import { getWearPercent } from '../getWearPercent';

type Trigger = {
    partId: PartId;
    urgency: Urgency;
    reason: string;
};

const PERCENT_MULTIPLIER = 100;

const RECOMMEND_PERCENT = RECOMMEND_THRESHOLD * PERCENT_MULTIPLIER;

const warningPartTriggers: Array<{ match: string; partId: PartId; label: string }> = [
    { label: 'ESC warning', match: 'esc', partId: 'esc' },
    { label: 'Motor warning', match: 'motor', partId: 'motor' },
    { label: 'Battery warning', match: 'battery pack', partId: 'battery' },
    { label: 'RTK warning', match: 'rtk', partId: 'rtkAntenna' }
];

const aggregatePartTriggers: Array<{ part: PartType; partId: PartId; label: string }> = [
    { label: 'Props', part: 'propSet', partId: 'propSet' },
    { label: 'Nozzles', part: 'nozzleSet', partId: 'nozzleSet' }
];

const perUnitPartTriggers: Array<{
    intervalPart: PartType;
    partId: PartId;
    label: string;
    usages: (drone: Drone) => number[];
}> = [
    {
        intervalPart: 'motor',
        label: 'Motor',
        partId: 'motor',
        usages: drone => drone.motors.map(motor => motor.usage)
    },
    {
        intervalPart: 'pumpSealKit',
        label: 'Pump',
        partId: 'pumpSealKit',
        usages: drone => drone.pumps.map(pump => pump.usage)
    },
    {
        intervalPart: 'flowMeter',
        label: 'Flow meter',
        partId: 'flowMeter',
        usages: drone => [drone.flowMeter.usage]
    }
];

const collectAggregateTriggers = ({
    drone,
    serviceIntervals
}: {
    drone: Drone;
    serviceIntervals: ServiceIntervalMap;
}): Trigger[] => {
    const triggers: Trigger[] = [];

    aggregatePartTriggers.forEach(entry => {
        const usage = drone.usage[entry.part] ?? 0;
        const wearStatus = getWearPercent({ interval: serviceIntervals[entry.part], usage });

        if (!wearStatus.configured || wearStatus.percentage < RECOMMEND_PERCENT) {
            return;
        }

        triggers.push({
            partId: entry.partId,
            reason: `${entry.label} ${Math.round(wearStatus.percentage)}%`,
            urgency: 'dueSoon'
        });
    });

    return triggers;
};

const collectPerUnitTriggers = ({
    drone,
    serviceIntervals
}: {
    drone: Drone;
    serviceIntervals: ServiceIntervalMap;
}): Trigger[] => {
    const triggers: Trigger[] = [];

    perUnitPartTriggers.forEach(entry => {
        const interval = serviceIntervals[entry.intervalPart];

        entry.usages(drone).forEach((usage, index) => {
            const wearStatus = getWearPercent({ interval, usage });

            if (!wearStatus.configured || wearStatus.percentage < RECOMMEND_PERCENT) {
                return;
            }

            triggers.push({
                partId: entry.partId,
                reason: `${entry.label} ${index + 1} ${Math.round(wearStatus.percentage)}%`,
                urgency: 'dueSoon'
            });
        });
    });

    return triggers;
};

const collectTriggersForDrone = ({
    drone,
    serviceIntervals
}: {
    drone: Drone;
    serviceIntervals: ServiceIntervalMap;
}): Trigger[] => {
    const triggers: Trigger[] = [];

    if (drone.topWarning) {
        const lowered = drone.topWarning.toLowerCase();

        warningPartTriggers.forEach(entry => {
            if (lowered.includes(entry.match)) {
                triggers.push({ partId: entry.partId, reason: entry.label, urgency: 'urgent' });
            }
        });
    }

    triggers.push(...collectAggregateTriggers({ drone, serviceIntervals }));
    triggers.push(...collectPerUnitTriggers({ drone, serviceIntervals }));

    return triggers;
};

export const computeRecommendations = ({
    drones,
    serviceIntervals
}: {
    drones: Drone[];
    serviceIntervals: ModelServiceIntervalMap;
}): Recommendation[] => {
    const byPart = new Map<PartId, Recommendation>();

    drones.forEach(drone => {
        const droneTriggers = collectTriggersForDrone({
            drone,
            serviceIntervals: getDroneIntervals({ drone, serviceIntervals })
        });

        droneTriggers.forEach(trigger => {
            const existing = byPart.get(trigger.partId);

            if (!existing) {
                byPart.set(trigger.partId, {
                    droneIds: [drone.id],
                    partId: trigger.partId,
                    qty: 1,
                    reason: trigger.reason,
                    urgency: trigger.urgency
                });

                return;
            }

            existing.qty += 1;

            if (!existing.droneIds.includes(drone.id)) {
                existing.droneIds.push(drone.id);
            }

            if (trigger.urgency === 'urgent') {
                existing.urgency = 'urgent';
                existing.reason = trigger.reason;
            }
        });
    });

    return Array.from(byPart.values());
};
